import React, { Fragment, ReactNode } from "react";
import { Box, Button, Popover, SxProps, Theme } from "@mui/material";
import { styled } from "@mui/material/styles";
import theme from "theme";

export interface Position {
  start: number;
  end: number;
}

interface Split {
  mark: boolean;
  start: number;
  end: number;
  content: string;
}

const splitWithOffsets = (text: string, offsets: Position[]): Split[] => {
  let lastEnd = 0;
  const splits: Split[] = [];

  const noOverlap: Position[] = [];
  offsets
    .sort((a, b) => a.start - b.start)
    .forEach((offset) => {
      if (
        noOverlap.findIndex((o) => o.start === offset.start) === -1 &&
        noOverlap.findIndex((o) => o.end === offset.end) === -1
      ) {
        noOverlap.push(offset);
      }
    });
  for (const offset of noOverlap) {
    const { start, end } = offset;
    if (lastEnd < start) {
      splits.push({
        start: lastEnd,
        end: start,
        mark: false,
        content: text.slice(lastEnd, start),
      });
    }
    splits.push({
      start: start,
      end: end,
      mark: true,
      content: text.slice(start, end),
    });
    lastEnd = end;
  }
  if (lastEnd < text.length) {
    splits.push({
      start: lastEnd,
      end: text.length,
      mark: false,
      content: text.slice(lastEnd, text.length),
    });
  }

  return splits;
};

interface NodeProps {
  split: Split;
  popover?: ReactNode;
  color?: string;
  highlight?: boolean;
  tag?: string;
  onClick: (split: Split) => void;
}

const Mark = styled("span")<{ color?: string; highlight?: boolean; popover?: ReactNode }>(({ color, highlight }) => ({
  backgroundImage: `linear-gradient(to right, ${color || "#84d2ff"} 100%, ${color || "#84d2ff"} 100%)`,
  backgroundPosition: "0 1.1em",
  backgroundRepeat: "repeat-x",
  backgroundSize: "8px 2px",
  borderStyle: "hidden",
  fontWeight: "bold",
  borderSize: 1,
  backgroundColor: highlight ? color : undefined,
  "&:hover": {
    cursor: ({ popover }) => (popover ? "pointer" : undefined),
  },
}));
const Node = (props: NodeProps) => {
  const { start, end, mark, content } = props.split;
  const { color, highlight, popover } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(undefined);
  const [open, setOpen] = React.useState(false);

  const openPopover = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  const closePopover = () => {
    setOpen(false);
    setAnchorEl(undefined);
  };

  if (mark)
    return (
      <Fragment>
        {props.popover && (
          <Popover
            transitionDuration={100}
            sx={{
              marginRight: theme.spacing(1),
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            onClose={closePopover}>
            <Button onClick={closePopover}>X</Button>
            {props.popover}
          </Popover>
        )}
        <Mark
          data-start={start}
          data-end={end}
          onClick={openPopover}
          color={color}
          highlight={highlight}
          popover={popover}>
          {content}
          {props.tag && (
            <span
              style={{
                fontSize: "0.5em",
                fontWeight: 500,
                fontVariant: "small-caps",
                marginLeft: 6,
                borderRadius: 2,
                backgroundColor: "#F5F5F5",
              }}>
              {props.tag}
            </span>
          )}
        </Mark>
      </Fragment>
    );

  return (
    <span data-start={start} data-end={end}>
      {content}
    </span>
  );
};

export interface Annotation {
  start: number;
  end: number;
  tag: string;
  color: string;
  highlight?: boolean;
  popover?: ReactNode;
}
export interface RainbowTextProps {
  text: string;
  annotations: Annotation[];
  className?: string;
  onClick?: (split: Split) => void;
  sx?: SxProps<Theme>;
}
const RainbowText = (props: RainbowTextProps) => {
  const rootRef = React.createRef<HTMLDivElement>();

  const getAnnotation = (split: Split) => {
    const idx = props.annotations.findIndex((a, _idx) => a.start === split.start && a.end === split.end);
    return props.annotations[idx];
  };

  const splits = splitWithOffsets(props.text, props.annotations);

  return (
    <Box ref={rootRef} sx={props.sx} className={props.className}>
      {splits.map((split) => {
        const annotation = getAnnotation(split);
        return (
          <Node
            key={`${annotation?.tag}-${split.start}-${split.end}`}
            split={split}
            popover={annotation?.popover}
            color={annotation?.color}
            highlight={annotation?.highlight}
            tag={annotation?.tag}
            onClick={
              props.onClick
                ? props.onClick
                : (_postion: Position) => {
                    return;
                  }
            }
          />
        );
      })}
    </Box>
  );
};

export default RainbowText;
